import {
  IconIllustrationSmallPolaroids,
  IconIllustrationSmallDollarBill,
  IconIllustrationSmallStar,
  IconIllustrationSmallSeniorCare,
  Icon24UtilityInfo,
} from '@care/react-icons';
import { Box, Container, Typography } from '@mui/material';
import sanitizeHtml from 'sanitize-html';
import he from 'he';
import { useState } from 'react';
import { seoSegmentationListingPage_seoSegmentationStats_SEOSegmentationStatsSuccess_caregiverStats as SEOSegmentationCaregiverStats } from '@/__generated__/seoSegmentationListingPage';
import { SEOSegmentationPageType, SEOSegmentationSearchingFor } from '@/__generated__/globalTypes';
import { SxClassProps } from '@/types/common';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import { usePageAttributes } from '../contexts/PageAttributesContext';
import { useResponsive } from '../hooks/useResponsive';
import { SafetyModalWrapper } from '../SafetyModalWrapper';

const classes: SxClassProps = {
  icon: {
    width: {
      xs: '48px',
      md: '104px',
    },
    height: {
      xs: '48px',
      md: '104px',
    },
  },
  headline: {
    fontSize: {
      xs: 'h2.fontSize',
      md: 'display3.fontSize',
    },
    lineHeight: {
      xs: '26px',
      md: '42px',
    },
    fontWeight: 'bold',
  },
};

type StatsSectionProps = {
  avgPayRate: SEOSegmentationCaregiverStats['avgPayRate'];
  avgYearsOfExperience: SEOSegmentationCaregiverStats['avgYearsOfExperience'];
  avgReviewRating: SEOSegmentationCaregiverStats['avgReviewRating'];
  totalProviders: SEOSegmentationCaregiverStats['totalProviders'];
  slotProps?: {
    icons?: {
      [key: string]: { 'data-testid': string };
    };
  };
};

const StatsContentRibbon = ({
  avgPayRate,
  avgYearsOfExperience,
  avgReviewRating,
  totalProviders,
  slotProps,
}: StatsSectionProps) => {
  const { pageType, segmentNamePluralLowerCase, segmentNamePlural, city, state, searchingFor } =
    usePageAttributes();
  const { isSmallViewport, isMediumViewport } = useResponsive();

  const [openModal, setOpenModal] = useState<boolean>(false);

  const currentDate = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date()
  );
  const roundAvgReviewRating = avgReviewRating?.toFixed(1) ?? null;
  const roundAvgYearsOfExp = (avgYearsOfExperience && Math.floor(avgYearsOfExperience)) ?? null;
  const totalProvidersString = totalProviders?.toLocaleString();
  const isNationalPage = pageType === SEOSegmentationPageType.NATIONAL;
  const isProviderPage = searchingFor === SEOSegmentationSearchingFor.PROVIDER;
  const segmentName = isProviderPage ? segmentNamePluralLowerCase : segmentNamePlural;
  const nationalStats = {
    headline: `${segmentName} that you'll love`,
    totalProviders: `${totalProvidersString} ${segmentNamePluralLowerCase} are listed on Care.com`,
    avgPayRate: `The average rate is $${avgPayRate?.amount}/hr as of ${currentDate}`,
    avgReviewRating: `The average star rating for ${segmentNamePluralLowerCase} is ${roundAvgReviewRating}`,
  };

  const localStats = {
    headline: `${segmentName} in ${city},&nbsp;${state} that you'll love`,
    totalProviders: `${totalProvidersString} ${segmentNamePluralLowerCase} are listed in ${city},&nbsp;${state}`,
    avgPayRate: `The average rate is $${avgPayRate?.amount}/hr as of ${currentDate}`,
    avgYearsOfExperience: `The average years of experience for ${segmentNamePluralLowerCase} is ${roundAvgYearsOfExp} years`,
  };

  const totalProviderStats = {
    key: 'totalProviders',
    national: nationalStats.totalProviders,
    local: localStats.totalProviders,
    icon: <IconIllustrationSmallPolaroids sx={classes.icon} {...slotProps?.icons?.polaroid} />,
  };

  const avgPayRateStats = {
    key: 'avgPayRate',
    national: nationalStats.avgPayRate,
    local: localStats.avgPayRate,
    icon: <IconIllustrationSmallDollarBill sx={classes.icon} {...slotProps?.icons?.dollar} />,
  };

  const avgReviewRatingStats = {
    key: 'avgReviewRating',
    national: nationalStats.avgReviewRating,
    local: null,
    icon: <IconIllustrationSmallStar sx={classes.icon} {...slotProps?.icons?.star} />,
  };

  const avgYearsOfExperienceStats = {
    key: 'avgYearsOfExperience',
    national: null,
    local: localStats.avgYearsOfExperience,
    icon: <IconIllustrationSmallSeniorCare sx={classes.icon} {...slotProps?.icons?.heart} />,
  };

  const nationalContent = [totalProviderStats, avgPayRateStats, avgReviewRatingStats];
  const localContent = [totalProviderStats, avgPayRateStats, avgYearsOfExperienceStats];
  const headline = isNationalPage ? nationalStats.headline : localStats.headline;
  const content = isNationalPage ? nationalContent : localContent;

  const handleSafetyModal = () => {
    setOpenModal(true);

    trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, {
      feature_name: 'background check banner',
    });
  };

  return (
    <>
      <Container
        maxWidth="md"
        sx={{
          borderRadius: 4,
          boxShadow: '0 0 16px 0 rgba(0, 0, 0, 0.15)',
          mt: 4,
          mb: {
            xs: 6,
            sm: 3,
            md: 8,
          },
          mx: {
            xs: 3,
            md: 'auto',
          },
          px: {
            xs: 3,
            md: 10,
          },
          py: {
            xs: 3,
            md: 4,
          },
          maxWidth: {
            md: 'calc(100vw - 5%)',
            lg: '1080px',
          },
        }}>
        <Box display="flex" justifyContent={{ xs: 'center' }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{ pb: 3, pr: { xs: 1, md: 0 }, maxWidth: { md: '780px' } }}>
            {isProviderPage && (
              <>
                <Typography
                  component="span"
                  sx={{
                    backgroundColor: 'care.blue.100',
                    borderRadius: 2,
                    px: 1,
                    ml: {
                      xs: -1,
                    },
                    ...classes.headline,
                  }}>
                  Background-checked
                </Typography>{' '}
              </>
            )}
            <Typography
              component="span"
              sx={{
                m: 0,
                display: 'inline',
                ...classes.headline,
              }}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(he.decode(headline)),
              }}
            />
          </Typography>
          <Box px={{ md: 2 }} mt={{ sm: -0.5, md: 0 }} onClick={handleSafetyModal}>
            <Icon24UtilityInfo
              size={isSmallViewport || isMediumViewport ? 32 : 40}
              sx={{
                cursor: 'pointer',
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: 'care.grey.100',
                },
              }}
              {...slotProps?.icons?.info}
            />
          </Box>
        </Box>
        <Box
          mx={{
            sm: '20%',
            md: 'auto',
          }}
          display="flex"
          flexDirection={{
            xs: 'column',
            md: 'row',
          }}
          justifyContent={{
            xs: 'center',
            md: 'space-between',
          }}
          alignItems={{
            md: 'center',
          }}>
          {content.map(({ key, national, local, icon }) => (
            <Box
              key={key}
              pb={{ xs: 3, md: 0 }}
              display="flex"
              alignItems="center"
              flexDirection={{ md: 'column' }}
              maxWidth={{
                md: '275px',
              }}
              px={{
                md: 3,
              }}>
              <Box pr={{ xs: 3.5, md: 0 }}>{icon}</Box>
              <Typography
                variant="body1"
                textAlign={{ md: 'center' }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    he.decode(isNationalPage ? (national ?? '') : (local ?? ''))
                  ),
                }}
              />
            </Box>
          ))}
        </Box>
      </Container>
      <SafetyModalWrapper openModal={openModal} setOpenModal={setOpenModal} />
    </>
  );
};

export { StatsContentRibbon };
